import React, { JSX } from 'react';
import sharedComboBoxStyles from './styles/Editor.module.scss';
import { IEditorProps } from './interfaces/IEditorProps';
import ImageDialog from './ImageDialog';
import QuillEditor from './QuillEditor';
import { css, Spinner, SpinnerSize, ComboBox, IComboBoxOption, Toggle } from '@fluentui/react';

interface ISiteData {
  Id: number;
  Title: string;
  Slug: string;
  Description: string;
  Text: string;
  Type: 'Node' | 'Category';
  Banner: string;
  Icon: string;
  Variante: string;
  Parent: number | null;
  Top: number | null;
  GalleryPath: string;
}

type SiteFormType = 'node' | 'category';
type ImageTarget = 'banner' | 'icon' | 'gallery';

const DEFAULT_CATEGORY_IMAGE = '/styles/images/jpgpng/home_banner.jpg';

function ParentCategoryComboBox(props: {
  selectedKey: number | null;
  options: IComboBoxOption[];  
  onSelect: (key: number) => void;
}): JSX.Element {
 
  const sharedComboBoxStyles = {
    container: {
      selectors: {
        '& .ms-Label': {
          color: `${UI_COLORS.text} !important`,
          fontFamily: UI_FONT,
        },
      },
      "& *:after": {
        border: "none !important"
      }
    },
    root: {
      background: UI_COLORS.card,
      border: `1px solid ${UI_COLORS.border} !important`,
      height: 39,
      selectors: {
        '&:after': {
          border: `1px solid ${UI_COLORS.border} !important`,
        },
      },
      "&:after": {
        border: "none !important"
      }
    },
    rootHovered: {
      background: UI_COLORS.card,
      border: `1px solid ${UI_COLORS.borderStrong} !important`,
      color: UI_COLORS.accentHover,
      /*selectors: {
        '&:hover:after': {
          border: `1px solid ${UI_COLORS.borderStrong} !important`,
        }
      },*/
      '&:hover .ms-ComboBox-Input':{
        color: UI_COLORS.text,
      }
    },
    rootFocused: {
      background: UI_COLORS.card,
      border: `1px solid ${UI_COLORS.borderStrong} !important`,
      //boxShadow: `0 0 0 1px ${UI_COLORS.borderStrong} inset`,
      selectors: {
        '&:focus:after': {
          border: `1px solid ${UI_COLORS.borderStrong} !important`,
        },
      },
    },
    input: {
      background: `${UI_COLORS.card} !important`,
      color: `${UI_COLORS.text} !important`,
      fontFamily: UI_FONT,
      selectors: {
        ':hover': {
          color: `${UI_COLORS.text} !important`,
        },
      },
    },
    callout: {
      background: '#1a0f08',
      border: `1px solid ${UI_COLORS.border}`,
    },
    optionsContainerWrapper: {
      background: '#1a0f08',
    },
    optionsContainer: {
      background: '#1a0f08',
    },
    caretDownButton:{
      root: {
        color: UI_COLORS.text,
        background: "rgba(0,0,0,0.4) !important",
        "&:hover": {
          color: UI_COLORS.borderStrong,
          background: "rgba(0,0,0,0.4)"
        },
        "&:active": {
          color: UI_COLORS.borderStrong,
          background: "rgba(0,0,0,0.4)"
        }
      },
      rootHovered: {
        color: UI_COLORS.borderStrong,
        background: "rgba(0,0,0,0.4)"
      },
      rootFocus: {
        color: UI_COLORS.borderStrong,
        background: "rgba(0,0,0,0.4)"
      }
    },
    comboBoxOptionStyles: {
      root: {
        color: UI_COLORS.text,        
        selectors: {
          '&:hover': { 
            background: UI_COLORS.text, 
            color: UI_COLORS.accentBgHover 
          },
        },
        "&.ms-Button--action":{
          color: UI_COLORS.text 
        },
         "&.ms-Button--action:hover":{
          color: UI_COLORS.accentBgHover,
          background: UI_COLORS.text, 
        },
         "&.ms-Button--action:active, &.ms-Button--action:focus":{
          color: UI_COLORS.accentBgHover,
          background: UI_COLORS.accent, 
        },
        "&.ms-Button--action.is-checked": {
          color: UI_COLORS.accentBgHover
        },
        "&.ms-Button--action.is-checked:hover": {
          color: UI_COLORS.dangerBgHover
        }
      },    
      optionText: { 
        fontFamily: UI_FONT,        
      }
    }
  };
  
  const { selectedKey, options, onSelect } = props;
  const [filterText, setFilterText] = React.useState('');

  const filteredOptions = React.useMemo(() => {
    const lc = filterText.trim().toLowerCase();
    if (!lc) return options;
    return options.filter((o) => o.key === 0 || String(o.text).toLowerCase().includes(lc));
  }, [options, filterText]);

  return (
    <ComboBox
      selectedKey={selectedKey}
      options={filteredOptions}
      allowFreeform
      autoComplete="on"
      useComboBoxAsMenuWidth
      styles={sharedComboBoxStyles}
      caretDownButtonStyles={sharedComboBoxStyles.caretDownButton}
      comboBoxOptionStyles={sharedComboBoxStyles.comboBoxOptionStyles}
      onInputValueChange={(value) => setFilterText(value ?? '')}
      onMenuDismissed={() => setFilterText('')}
      onChange={(_e, option) => {
        if (option) {
          setFilterText('');
          onSelect(Number(option.key) || 0);
        }
      }}
      onRenderOption={(item) => {
        const depth: number = (item?.data as { depth?: number })?.depth ?? 0;
        return (
          <span style={{ paddingLeft: depth * 16 }}>
            {item?.text}
          </span>
        );
      }}
    />
  );
}

interface IGroup {
  id: number;
  name: string;
}

interface IEditorState {
  isLoading: boolean;
  loadError: string | null;
  isContentMod: boolean;
  site: ISiteData | null;
  allCategories: IComboBoxOption[];
  allGroups: IGroup[];
  formReadGroupIds: number[];
  formType: SiteFormType;
  formTitle: string;
  formSlug: string;
  formDescription: string;
  formText: string;
  formParentId: number;
  formBanner: string;
  formIcon: string;
  formGalleryPath: string;
  isSaving: boolean;
  flyinMessage: string;
  flyinIsError: boolean;
  flyinTimer: number | null;
  showDeleteConfirm: boolean;
  imageDialogTarget: ImageTarget | null;
  slugError: string;
  isCheckingSlug: boolean;
  slugManuallyEdited: boolean;
}

const UI_COLORS = {
  bg: '#000',
  panel: '#000',
  card: 'rgba(0,0,0,.4)',
  border: '#ffd36b',
  borderStrong: '#c77e19',
  text: '#fdf2b4',
  accent: '#f7b10a',
  accentHover: '#ffd36b',
  accentBgHover: '#b06601',
  dangerBgHover: '#9f1600',
};

const UI_FONT = '"Marmelad", sans-serif';

export default class Editor extends React.Component<IEditorProps, IEditorState> {
  private slugCheckRequest = 0;

  constructor(props: IEditorProps) {
    super(props);
    this.state = {
      isLoading: true,
      loadError: null,
      isContentMod: false,
      site: null,
      allCategories: [],
      allGroups: [],
      formReadGroupIds: [],
      formType: 'node',
      formTitle: '',
      formSlug: '',
      formDescription: '',
      formText: '',
      formParentId: 0,
      formBanner: DEFAULT_CATEGORY_IMAGE,
      formIcon: DEFAULT_CATEGORY_IMAGE,
      formGalleryPath: '',
      isSaving: false,
      flyinMessage: '',
      flyinIsError: false,
      flyinTimer: null,
      showDeleteConfirm: false,
      imageDialogTarget: null,
      slugError: '',
      isCheckingSlug: false,
      slugManuallyEdited: false,
    };
  }

  componentDidMount(): void {
    if (this.props.openEdior) {
      this.initEditor();
    }
  }

  componentWillUnmount(): void {
    if (this.state.flyinTimer !== null) {
      window.clearTimeout(this.state.flyinTimer);
    }
  }

  private isAddMode = (): boolean => {
    return /&add(?:=[^&]*)?$/i.test(window.location.pathname) ||
      new URLSearchParams(window.location.search).has('add');
  };

  private getSiteSlug = (): string => {
    let path = window.location.pathname.replace(/^\/+|\/+$/g, '');
    path = path.replace(/\/\d+\/?$/, '');

    const firstSegment = decodeURIComponent(path.split('/')[0] ?? '');
    const cleanedSegment = firstSegment.replace(/&editor(?:=.*)?$/i, '').replace(/&add(?:=.*)?$/i, '').trim();
    if (cleanedSegment !== '') {
      return cleanedSegment;
    }

    const querySite = new URLSearchParams(window.location.search).get('site') ?? '';
    return querySite.replace(/&editor(?:=.*)?$/i, '').replace(/&add(?:=.*)?$/i, '').trim();
  };

  private flattenCategories = (cats: ISiteData[], depth = 0): IComboBoxOption[] => {
    const result: IComboBoxOption[] = [];
    for (const cat of cats) {
      if ((cat as unknown as { Type: string }).Type !== 'Category') {
        continue;
      }
      result.push({ key: cat.Id, text: this.decodeHtml(cat.Title), data: { depth } });
      const children = (cat as unknown as { children?: ISiteData[] }).children;
      if (Array.isArray(children) && children.length > 0) {
        result.push(...this.flattenCategories(children, depth + 1));
      }
    }
    return result;
  };

  private findParentCategoryIdForNode = (site: ISiteData | null, categories: ISiteData[]): number => {
    if (!site || site.Type !== 'Node') {
      return 0;
    }

    const walk = (cats: ISiteData[]): number => {
      for (const cat of cats) {
        const catNodes = (cat as unknown as { nodes?: Array<{ Id?: number; Slug?: string }> }).nodes;
        if (Array.isArray(catNodes)) {
          const match = catNodes.find((n) => n?.Id === site.Id || n?.Slug === site.Slug);
          if (match) {
            return cat.Id;
          }
        }

        const children = (cat as unknown as { children?: ISiteData[] }).children;
        if (Array.isArray(children) && children.length > 0) {
          const nested = walk(children);
          if (nested > 0) {
            return nested;
          }
        }
      }

      return 0;
    };

    return walk(categories);
  };

  private sanitizeSlug = (value: string): string => {
    const umlautMap: Record<string, string> = {
      ä: 'a',
      ö: 'o',
      ü: 'u',
      Ä: 'a',
      Ö: 'o',
      Ü: 'u',
      ß: 's',
    };

    return value
      .replace(/[äöüÄÖÜß]/g, (char) => umlautMap[char] ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  private buildSlug = (title: string): string => {
    return this.sanitizeSlug(title);
  };

  private validateSlugUniqueness = async (slugToCheck?: string): Promise<boolean> => {
    const { site, formType } = this.state;
    const slug = this.sanitizeSlug(slugToCheck ?? this.state.formSlug);

    if (!slug) {
      this.setState({ formSlug: slug, slugError: 'Slug ist erforderlich', isCheckingSlug: false });
      return false;
    }

    const requestId = ++this.slugCheckRequest;
    this.setState({ formSlug: slug, slugError: '', isCheckingSlug: true });

    try {
      const params = new URLSearchParams({
        resource: 'check-slug',
        slug,
        type: formType,
      });

      if (site?.Id) {
        params.set('id', String(site.Id));
      }

      const resp = await fetch(`/api/content?${params.toString()}`);
      const data = await resp.json();

      if (requestId !== this.slugCheckRequest) {
        return false;
      }

      const isUnique = resp.ok && data?.success && data?.isUnique === true;
      const slugError = isUnique ? '' : (data?.error ?? 'Slug ist bereits vorhanden');
      this.setState({
        isCheckingSlug: false,
        slugError,
      });

      if (!isUnique) {
        this.showFlyin(slugError, true);
      }

      return isUnique;
    } catch {
      if (requestId === this.slugCheckRequest) {
        this.setState({
          isCheckingSlug: false,
          slugError: 'Slug konnte nicht geprüft werden',
        });
      }
      return false;
    }
  };

  private decodeHtml = (value: string): string => {
    if (!value || typeof window === 'undefined') {
      return value;
    }

    // Only decode if content appears to have HTML entities (named or numeric)
    if (!/&[a-z#][a-z0-9]+;/i.test(value)) {
      return value;
    }

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  };

  private encodeHtml = (value: string): string => {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/ä/g, '&auml;')
      .replace(/ö/g, '&ouml;')
      .replace(/ü/g, '&uuml;')
      .replace(/Ä/g, '&Auml;')
      .replace(/Ö/g, '&Ouml;')
      .replace(/Ü/g, '&Uuml;')
      .replace(/ß/g, '&szlig;');
  };

  private showFlyin = (message: string, isError = false): void => {
    if (this.state.flyinTimer !== null) {
      window.clearTimeout(this.state.flyinTimer);
    }
    const timer = window.setTimeout(() => this.setState({ flyinMessage: '' }), 3500);
    this.setState({ flyinMessage: message, flyinIsError: isError, flyinTimer: timer });
  };

  private normalizeRichText = (html: string): string => {
    if (!html) {
      return '';
    }
    return html.replace(/\u00A0/g, ' ').trim();
  };

  private isRichTextEmpty = (html: string): boolean => {
    const normalized = this.normalizeRichText(html);
    if (!normalized) {
      return true;
    }

    if (typeof window === 'undefined') {
      // Conservative fallback for non-browser contexts.
      const stripped = normalized
        .replace(/<br\s*\/?>/gi, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/<[^>]*>/g, '')
        .trim();
      return stripped === '';
    }

    const container = document.createElement('div');
    container.innerHTML = normalized;
    const plain = (container.textContent ?? '').replace(/\u00A0/g, ' ').trim();
    return plain === '';
  };

  private getContentFromDOM = (): { id: number; type: 'node' | 'category' } | null => {
    // Lese contentId und contentType aus data-Attributen der article-Tag
    const article = document.querySelector('article[data-mode="editor"]');
    if (!article) {
      return null;
    }

    const contentId = article.getAttribute('data-content-id');
    const contentType = article.getAttribute('data-content-type');

    if (!contentId || !contentType) {
      return null;
    }

    const id = parseInt(contentId, 10);
    if (isNaN(id) || id <= 0) {
      return null;
    }

    return {
      id,
      type: (contentType === 'node' ? 'node' : 'category') as 'node' | 'category',
    };
  };

  private initEditor = async (): Promise<void> => {
    const slug = this.getSiteSlug();
    const addMode = this.isAddMode();
    const contentApi = '/api/content';
    
    // Wenn slug leer ist, versuche content-ID aus DOM zu lesen
    let siteResPromise: Promise<{ success: boolean; data: ISiteData | null } | null>;
    if (slug) {
      siteResPromise = fetch(`${contentApi}?resource=site&slug=${encodeURIComponent(slug)}`).then((r) =>
        r.ok ? r.json() : null,
      );
    } else if (!addMode) {
      // Kein slug, aber auch nicht im add-Modus - versuche content-ID aus DOM
      const domContent = this.getContentFromDOM();
      if (domContent) {
        const typeToFetch = domContent.type === 'node' ? 'node' : 'category';
        siteResPromise = fetch(`${contentApi}?resource=site&id=${domContent.id}&type=${typeToFetch}`).then((r) =>
          r.ok ? r.json() : null,
        );
      } else {
        siteResPromise = Promise.resolve(null);
      }
    } else {
      siteResPromise = Promise.resolve(null);
    }

    try {
      const [modRes, siteRes, allRes, groupsRes] = await Promise.all([
        fetch(`${contentApi}?resource=is-content-mod`).then((r) => r.json()),
        siteResPromise,
        fetch(`${contentApi}?resource=all`).then((r) => r.json()),
        fetch(`${contentApi}?resource=groups`).then((r) => r.ok ? r.json() : { success: false, data: [] }),
      ]);

      const isContentMod: boolean = modRes?.isContentModerator === true;
      const rawCats: ISiteData[] = allRes?.success ? allRes.data : [];
      const allGroups: IGroup[] = groupsRes?.success ? (groupsRes.data as IGroup[]) : [];

      if (addMode) {
        // Add-Modus: aktuelle Seite nur für Eltern-Kategorie nutzen, Formular leer lassen
        const currentSite: ISiteData | null = siteRes?.success ? siteRes.data : null;
        let parentId = 0;
        if (currentSite?.Type === 'Category') {
          parentId = currentSite.Id;
        } else if (currentSite) {
          parentId = this.findParentCategoryIdForNode(currentSite, rawCats);
        }
        this.setState({
          isLoading: false,
          isContentMod,
          site: null,
          allCategories: this.flattenCategories(rawCats),
          allGroups,
          formReadGroupIds: [],
          formType: 'node',
          formTitle: '',
          formSlug: '',
          formDescription: '',
          formText: '',
          formParentId: parentId,
          formBanner: DEFAULT_CATEGORY_IMAGE,
          formIcon: DEFAULT_CATEGORY_IMAGE,
          formGalleryPath: '',
          slugError: '',
          isCheckingSlug: false,
          slugManuallyEdited: false,
        });
      } else {
        const site: ISiteData | null = siteRes?.success ? siteRes.data : null;
        const resolvedParentId = site?.Type === 'Category'
          ? (site.Parent ?? 0)
          : this.findParentCategoryIdForNode(site, rawCats);

        // Bestehende Leserechte laden (nur im Edit-Modus)
        let readGroupIds: number[] = [];
        if (site?.Id) {
          const permType = site.Type === 'Category' ? 'category' : 'node';
          try {
            const permRes = await fetch(`${contentApi}?resource=read-permissions&type=${permType}&id=${site.Id}`);
            if (permRes.ok) {
              const permData = await permRes.json();
              if (permData?.success && Array.isArray(permData.data)) {
                readGroupIds = permData.data as number[];
              }
            }
          } catch {
            // Permissions-Ladefehler ist nicht kritisch
          }
        }

        this.setState({
          isLoading: false,
          isContentMod,
          site,
          allCategories: this.flattenCategories(rawCats),
          allGroups,
          formReadGroupIds: readGroupIds,
          formType: site?.Type === 'Category' ? 'category' : 'node',
          formTitle: this.decodeHtml(site?.Title ?? ''),
          formSlug: site?.Slug ?? slug,
          formDescription: this.decodeHtml(site?.Description ?? ''),
          formText: this.decodeHtml(site?.Text ?? ''),
          formParentId: resolvedParentId,
          formBanner: site?.Banner || DEFAULT_CATEGORY_IMAGE,
          formIcon: site?.Icon || DEFAULT_CATEGORY_IMAGE,
          formGalleryPath: site?.GalleryPath ?? '',
          slugError: '',
          isCheckingSlug: false,
          slugManuallyEdited: false,
        });
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Unbekannter Fehler';
      this.setState({ isLoading: false, loadError: 'Ladefehler: ' + msg });
    }
  };

  private handleTitleChange = (title: string): void => {
    this.setState((prev) => ({
      formTitle: title,
      formSlug: prev.site === null && !prev.slugManuallyEdited ? this.buildSlug(title) : prev.formSlug,
      slugError: prev.site === null && !prev.slugManuallyEdited ? 'Slug noch nicht geprüft' : prev.slugError,
    }));
  };

  private handleTitleBlur = (): void => {
    const { site, formSlug, formTitle, slugManuallyEdited } = this.state;
    const isAddMode = site === null;
    const slugToValidate = isAddMode && !slugManuallyEdited ? this.buildSlug(formTitle) : formSlug;
    this.setState({ formSlug: slugToValidate }, () => {
      void this.validateSlugUniqueness(slugToValidate);
    });
  };

  private handleSlugChange = (slug: string): void => {
    this.setState({
      formSlug: this.sanitizeSlug(slug),
      slugManuallyEdited: true,
      slugError: 'Slug noch nicht geprüft',
    });
  };

  private handleSlugBlur = (): void => {
    void this.validateSlugUniqueness(this.state.formSlug);
  };

  private handleSave = async (): Promise<void> => {
    const {
      formType,
      formTitle,
      formSlug,
      formDescription,
      formText,
      formParentId,
      formBanner,
      formIcon,
      formGalleryPath,
      formReadGroupIds,
      site,
      isCheckingSlug,
    } = this.state;

    if (!formTitle.trim()) {
      this.showFlyin('Titel ist erforderlich', true);
      return;
    }

    if (!formSlug.trim()) {
      this.showFlyin('Slug ist erforderlich', true);
      return;
    }

    if (isCheckingSlug) {
      this.showFlyin('Slug wird noch geprüft', true);
      return;
    }

    const slugIsUnique = await this.validateSlugUniqueness(formSlug);
    if (!slugIsUnique) {
      this.showFlyin(this.state.slugError || 'Slug ist bereits vorhanden', true);
      return;
    }

    const normalizedText = this.normalizeRichText(formText);
    if (this.isRichTextEmpty(normalizedText)) {
      this.showFlyin('Inhalt darf nicht leer sein', true);
      return;
    }

    this.setState({ isSaving: true });

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let body: Record<string, any>;

      if (formType === 'category') {
        body = {
          resource: 'save-category',
          id: site?.Id ?? 0,
          cat_name: this.encodeHtml(formTitle.trim()),
          cat_slug: formSlug,
          cat_short_description: formDescription,
          cat_description: normalizedText,
          cat_parent: formParentId > 0 ? formParentId : null,
          cat_banner: formBanner,
          cat_icon: formIcon,
          read_group_ids: formReadGroupIds,
        };
      } else {
        body = {
          resource: 'save-node',
          id: site?.Id ?? 0,
          node_titel: this.encodeHtml(formTitle.trim()),
          node_slug: formSlug,
          node_description: formDescription,
          node_text: normalizedText,
          parent_category_id: formParentId > 0 ? formParentId : 0,
          node_gallery_path: formGalleryPath.trim() || null,
          read_group_ids: formReadGroupIds,
        };
        
        // node_variant: Nur beim Erstellen eines neuen Nodes setzen
        if (!site?.Id) {
          // Neuer Node: Auf default setzen
          body.node_variant = 'default';
        } else if (site.Variante) {
          // Existierender Node: Die aktuelle node_variant beibehalten
          body.node_variant = site.Variante;
        }
      }

      const resp = await fetch('/api/content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json();

      if (resp.ok && data.success) {
        this.showFlyin('Gespeichert!');
        if (!site && data.data?.id) {
          if (formType === 'node') {
            // Im Editor bleiben, in den Edit-Modus wechseln
            const newSite: ISiteData = {
              Id: data.data.id,
              Title: formTitle,
              Slug: formSlug,
              Description: formDescription,
              Text: formText,
              Type: 'Node',
              Banner: formBanner,
              Icon: formIcon,
              Variante: 'default',
              Parent: formParentId || null,
              Top: null,
              GalleryPath: formGalleryPath,
            };
            this.setState({ site: newSite });
            const newUrl = window.location.href
              .replace(/([?&])add(?:=[^&#]*)?((?=&)|$)/gi, '$2')
              .replace(/[?&]$/, '')
              .replace(/\?&/, '?');
            window.history.replaceState(null, '', newUrl || '/');
          } else {
            window.setTimeout(() => {
              window.location.href = '/' + formSlug;
            }, 1400);
          }
        }
      } else {
        this.showFlyin(data.error ?? 'Speichern fehlgeschlagen', true);
      }
    } catch {
      this.showFlyin('Netzwerkfehler beim Speichern', true);
    } finally {
      this.setState({ isSaving: false });
    }
  };

  private handleBack = (): void => {
    const cleaned = window.location.href
      .split('#')[0]
      .replace(/([?&])editor(?:=[^&#]*)?((?=&)|$)/gi, '$2')
      .replace(/([?&])add(?:=[^&#]*)?((?=&)|$)/gi, '$2')
      .replace(/[?&]$/, '')
      .replace(/\?&/, '?');

    window.location.href = cleaned || '/';
  };

  private handleDeleteConfirm = async (): Promise<void> => {
    const { formType, formText, site } = this.state;
    if (!site) {
      return;
    }

    this.setState({ showDeleteConfirm: false, isSaving: true });

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const body: Record<string, any> =
        formType === 'category'
          ? {
              resource: 'save-category',
              id: site.Id,
              cat_name: site.Title,
              cat_slug: site.Slug,
              cat_status: 'paperpin',
            }
          : {
              resource: 'save-node',
              id: site.Id,
              node_titel: site.Title,
              node_slug: site.Slug,
              node_text: formText,
              node_type: 'paperpin',
            };

      const resp = await fetch('/api/content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json();

      if (resp.ok && data.success) {
        this.showFlyin('Gelöscht!');
        window.setTimeout(() => {
          window.location.href = '/';
        }, 1400);
      } else {
        this.showFlyin(data.error ?? 'Löschen fehlgeschlagen', true);
      }
    } catch {
      this.showFlyin('Netzwerkfehler beim Löschen', true);
    } finally {
      this.setState({ isSaving: false });
    }
  };

  private handleImageDialogSelect = (url: string): void => {
    const selectedUrl = url || DEFAULT_CATEGORY_IMAGE;
    if (this.state.imageDialogTarget === 'banner') {
      this.setState({ formBanner: selectedUrl, imageDialogTarget: null });
    } else if (this.state.imageDialogTarget === 'icon') {
      this.setState({ formIcon: selectedUrl, imageDialogTarget: null });
    } else if (this.state.imageDialogTarget === 'gallery') {
      // currentPath von ImageDialog ist relativ zu content/upload/ (z.B. "images/Test")
      // Für node_gallery_path brauchen wir den Pfad relativ zu htdocs
      const uploadPrefix = 'content/upload/';
      const normalized = url.replace(/^\/+/, '');
      const galleryPath = normalized.startsWith(uploadPrefix) || normalized === ''
        ? normalized
        : uploadPrefix + normalized;
      this.setState({ formGalleryPath: galleryPath, imageDialogTarget: null });
    }
  };

  private isDefaultImage = (url: string): boolean => {
    if (!url) {
      return true;
    }

    const normalized = url
      .replace(/^https?:\/\/[^/]+/i, '')
      .split(/[?#]/)[0]
      .replace(/\/+$/g, '');

    return normalized === DEFAULT_CATEGORY_IMAGE;
  };

  private resetImageToDefault = (target: ImageTarget): void => {
    if (target === 'banner') {
      this.setState({ formBanner: DEFAULT_CATEGORY_IMAGE });
    } else {
      this.setState({ formIcon: DEFAULT_CATEGORY_IMAGE });
    }
  };

  private renderImagePicker(label: string, url: string, target: ImageTarget): JSX.Element {
    const effectiveUrl = url || DEFAULT_CATEGORY_IMAGE;
    const hasCustomImage = !this.isDefaultImage(url);
    const backgroundStyle = {
      backgroundImage: `linear-gradient(rgba(0,0,0,0.18), rgba(0,0,0,0.18)), url(${effectiveUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };

    return (
      <div className={sharedComboBoxStyles.formGroup}>
        <label className={sharedComboBoxStyles.formLabel}>{label}</label>
        <article className={`cat-node-link ${sharedComboBoxStyles.imageCard}`} style={backgroundStyle}>
          <div className="stories mw-flexbox mw-vertical-center">
            <div className="mw-flex-1" style={{ height: '100%', maxWidth: 'calc(100% - 90px)', padding: '6px 0' }}>
              <span style={{ color: '#f9f6ee', fontSize: '0.85em', textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>
                {hasCustomImage ? `${label} gewählt` : 'Standardbild aktiv'}
              </span>
            </div>
            <div className="mw-flex-right  mw-flexbox" style={{ flexDirection: 'column-reverse', gap: 8 }}>
              {hasCustomImage && (
                <button
                  type="button"
                  className="mw-button mw-button--ghost mw-button--small mw-button--warn"
                  onClick={() => this.resetImageToDefault(target)}
                >
                  <div className="mw-button-outer">
                    <div className="mw-button-inner">Löschen</div>
                  </div>
                </button>
              )}
              <button
                type="button"
                className="mw-button mw-button--yellow mw-button--ghost mw-button--small mw-button--warn"
                onClick={() => this.setState({ imageDialogTarget: target })}
              >
                <div className="mw-button-outer">
                  <div className="mw-button-inner">{hasCustomImage ? 'Ersetzen' : 'Auswählen'}</div>
                </div>
              </button>
            </div>
          </div>
        </article>
      </div>
    );
  }

  private renderForm(): JSX.Element {
    const {
      site,
      allCategories,
      allGroups,
      formReadGroupIds,
      formType,
      formTitle,
      formSlug,
      formDescription,
      formText,
      formGalleryPath,
      formParentId,
      formBanner,
      formIcon,
      isSaving,
      slugError,
      isCheckingSlug,
    } = this.state;

    const sharedToggleStyles = {
      root: {
        marginTop: 2,
      },      
      label: {
        color: UI_COLORS.text,
        fontFamily: UI_FONT,
      },
      text: {
        color: UI_COLORS.text,
        fontFamily: UI_FONT,
      },
      pill: {
        background: UI_COLORS.card,
        borderColor: UI_COLORS.border,
        selectors: {
          ':hover': {
            borderColor: UI_COLORS.accent,
            background: UI_COLORS.card,
          },
          ':active': {
            borderColor: UI_COLORS.accent,
            background: UI_COLORS.card,
          },
          ':focus': {
            borderColor: UI_COLORS.accent,
            background: UI_COLORS.card,
          },
          ':focus:after': {
            borderColor: UI_COLORS.accent,
            background: UI_COLORS.card
          },
        },
      },
      thumb: {
        background: UI_COLORS.text + " !important",
        ':hover': {            
          background: UI_COLORS.accent,
        },
        ':active': {            
          background: UI_COLORS.accent,
        },
        ':focus': {            
          background: UI_COLORS.accent,
        },
        ':focus:after': {            
          background: UI_COLORS.accent
        },
      },
  };
  const sharedToggleCheckedStyles = {
    root: {
      marginTop: 2,
    },      
    label: {
      color: UI_COLORS.text,
      fontFamily: UI_FONT,
    },
    text: {
      color: UI_COLORS.text,
      fontFamily: UI_FONT,
    },
    pill: {
      background: UI_COLORS.card,
      borderColor: UI_COLORS.accent,
      selectors: {
        ':hover': {
          borderColor: UI_COLORS.accentBgHover,
          background: UI_COLORS.card,
        },
        ':active': {
          borderColor: UI_COLORS.accentBgHover,
          background: UI_COLORS.card,
        },
        ':focus': {
          borderColor: UI_COLORS.accentBgHover,
          background: UI_COLORS.card,
        },
        ':focus:after': {
          borderColor: UI_COLORS.accentBgHover,
          background: UI_COLORS.card
        },
      },
    },
    thumb: {
      background: UI_COLORS.accent + " !important",
      ':hover': {            
        background: UI_COLORS.accentBgHover,
      },
      ':active': {            
        background: UI_COLORS.accentBgHover,
      },
      ':focus': {            
        background: UI_COLORS.accentBgHover,
      },
      ':focus:after': {            
        background: UI_COLORS.accentBgHover
      },
    },
  };

    const isNew = site === null;
    const isCategory = formType === 'category';
    return (
      <div className={css(sharedComboBoxStyles.editorContent, "mw-form","mw-form-rows")} style={{margin: 0}}>
        <div className={sharedComboBoxStyles.formGroup}>
          <label className={sharedComboBoxStyles.formLabel}>Art der Seite</label>
          <div className={css(sharedComboBoxStyles.radioGroup,"mw-fbox-text")}>
            <label className={sharedComboBoxStyles.radioButtons}>
              <input
                type="radio"
                value="node"
                checked={formType === 'node'}
                disabled={!isNew}
                onChange={() => this.setState({ formType: 'node' })}
              />
              <span>Text-Seite</span>
            </label>
            <label className={sharedComboBoxStyles.radioButtons}>
              <input
                type="radio"
                value="category"
                checked={formType === 'category'}
                disabled={!isNew}
                onChange={() => this.setState({ formType: 'category' })}
              />
              <span>Kategorie</span>
            </label>
          </div>
        </div>

        <div className={css(sharedComboBoxStyles.formGroup,"mw-fbox-text")}>
          <label className={sharedComboBoxStyles.formLabel}>Titel</label>
          <input
            type="text"
            className={sharedComboBoxStyles.formControl}
            value={formTitle}
            onChange={(e) => this.handleTitleChange(e.target.value)}
            onBlur={this.handleTitleBlur}
          />
        </div>

        <div className={css(sharedComboBoxStyles.formGroup,"mw-fbox-text")}>
          <label className={sharedComboBoxStyles.formLabel}>Slug</label>
          <input
            type="text"
            className={sharedComboBoxStyles.formControl}
            value={formSlug}
            onChange={(e) => this.handleSlugChange(e.target.value)}
            onBlur={this.handleSlugBlur}
          />
          {isCheckingSlug && <div className={sharedComboBoxStyles.errorMsg}>Slug wird geprüft...</div>}
        </div>

        <div className={css(sharedComboBoxStyles.formGroup, 'mw-fbox-text')}>
          <label className={sharedComboBoxStyles.formLabel}>Übergeordnete Kategorie</label>
          <ParentCategoryComboBox
            selectedKey={formParentId > 0 ? formParentId : null}
            options={[
              { key: 0, text: '— Keine —' },
              ...allCategories.filter((o) =>
                !(formType === 'category' && site && o.key === site.Id)
              ),
            ]}            
            onSelect={(key) => this.setState({ formParentId: key })}
          />
        </div>

        <div className={css(sharedComboBoxStyles.formGroup,"mw-fbox-text")}>
          <label className={sharedComboBoxStyles.formLabel}>Kurzbeschreibung</label>
          <textarea
            className={sharedComboBoxStyles.formControl}
            rows={3}
            value={formDescription}
            onChange={(e) => this.setState({ formDescription: e.target.value })}
          />
        </div>

        {isCategory && (
          <div className={sharedComboBoxStyles.formRow}>
            <div>{this.renderImagePicker('Banner', formBanner, 'banner')}</div>
            <div>{this.renderImagePicker('Icon', formIcon, 'icon')}</div>
          </div>
        )}

        <div className={sharedComboBoxStyles.formGroup}>
          <label className={sharedComboBoxStyles.formLabel}>{isCategory ? 'Beschreibungstext' : 'Inhalt'}</label>
          <QuillEditor value={formText} onChange={(v) => this.setState({ formText: v })} />
        </div>

        {!isCategory && (
          <div className={css(sharedComboBoxStyles.formGroup, 'mw-fbox-text')}>
            <label className={sharedComboBoxStyles.formLabel}>Galerie-Ordner</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="text"
                className={sharedComboBoxStyles.formControl}
                value={formGalleryPath}
                onChange={(e) => this.setState({ formGalleryPath: e.target.value })}
                placeholder="z.B. content/upload/images/MeinOrdner"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="mw-button mw-button--ghost mw-button--small"
                onClick={() => this.setState({ imageDialogTarget: 'gallery' })}
              >
                <div className="mw-button-outer"><div className="mw-button-inner">Suchen</div></div>
              </button>
              <button
                type="button"
                className="mw-button mw-button--ghost mw-button--small"
                onClick={() => this.setState({ formGalleryPath: '' })}
              >
                <div className="mw-button-outer"><div className="mw-button-inner">Löschen</div></div>
              </button>
            </div>
          </div>
        )}

        {allGroups.length > 0 && (
          <div className={css(sharedComboBoxStyles.formGroup, 'mw-fbox-text')}>
            <label className={sharedComboBoxStyles.formLabel}>Leserechte</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: '0.85em', color: '#666', marginBottom: 2 }}>
                Wenn keine Gruppe gewählt ist, ist der Inhalt öffentlich lesbar.
              </span>
              {allGroups.map((group) => {
                const checked = formReadGroupIds.includes(group.id);
                return (<>             
                    <Toggle
                      key={group.id}
                      label={group.name}
                      checked={checked}
                      styles={checked ? sharedToggleCheckedStyles : sharedToggleStyles}
                      onChange={(_, checked) =>
                        this.setState((prev) => ({
                          formReadGroupIds: checked
                            ? [...prev.formReadGroupIds, group.id]
                            : prev.formReadGroupIds.filter((id) => id !== group.id),
                        }))
                      }
                    /></>                                     
                );
              })}
            </div>
          </div>
        )}

        <div className={sharedComboBoxStyles.buttonBar}>
          <div className={sharedComboBoxStyles.buttonBarLeft}>
            <button
              type="button"
              className="mw-button mw-button--ghost mw-button--red mw-button--alert"
              disabled={isSaving || isCheckingSlug || !!slugError}
              onClick={this.handleSave}
            >
              <div className="mw-button-outer">
                <div className="mw-button-inner">{isSaving ? 'Speichert...' : isCheckingSlug ? 'Prüft Slug...' : 'Speichern'}</div>
              </div>
            </button>
            <button
              type="button"
              className="mw-button mw-button--ghost"
              disabled={isSaving}
              onClick={this.handleBack}
            >
              <div className="mw-button-outer"><div className="mw-button-inner">Zurück</div></div>
            </button>
          </div>
          <div className={sharedComboBoxStyles.buttonBarRight}>
            {site && (
              <button
                type="button"
                className="mw-button mw-button--ghost"
                disabled={isSaving}
                onClick={() => this.setState({ showDeleteConfirm: true })}
              >
                <div className="mw-button-outer"><div className="mw-button-inner">Löschen</div></div>
              </button>
            )}
          </div>
        </div>

      </div>
    );
  }

  public render(): React.ReactNode {
    const { showEdiorButton, openEdior } = this.props;
    
    if (showEdiorButton) {
      const currentUrl = window.location.href;
      const bodyBaseUrl = document.body.getAttribute('data-url') ?? '';

      const normalizeUrlForCompare = (url: string): string => {
        try {
          const parsed = new URL(url);
          const pathname = parsed.pathname.replace(/\/$/, '');
          return `${parsed.origin}${pathname}`;
        } catch {
          return url.replace(/\/$/, '');
        }
      };

      const hasEditorParam = /[?&]editor(?:=|&|$)/i.test(currentUrl) || /&editor(?:=|$)/i.test(currentUrl);
      // Auf der Startseite (Root-URL ohne Path) sollte ?editor verwendet werden
      const isDefaultUrl = normalizeUrlForCompare(currentUrl.split('?')[0].split('#')[0]) === normalizeUrlForCompare(bodyBaseUrl);

      const [urlWithoutHash, hashPart] = currentUrl.split('#', 2);
      const editorSuffix = isDefaultUrl ? '?editor' : '&editor';
      const url = hasEditorParam
        ? currentUrl
        : `${urlWithoutHash}${editorSuffix}${hashPart !== undefined ? `#${hashPart}` : ''}`;

      return (
        <div>
          <a
            href={url}
            style={{ width: 30, height: '30px' }}
            className="mw-button mw-button--ghost mw-button--red mw-button--small mw-button--alert mw-button--edit post-edit-button"
          >
            <div className="mw-button-outer">
              <div className="mw-button-inner"></div>
            </div>
          </a>
        </div>
      );
    }

    if (!openEdior) {
      return null;
    }
    
    const { isLoading, loadError, isContentMod, flyinMessage, flyinIsError, imageDialogTarget, showDeleteConfirm } = this.state;

    return (
      <div className={sharedComboBoxStyles.embeddedEditorRoot}>
        {flyinMessage && (
          <div className={`${sharedComboBoxStyles.flyin}${flyinIsError ? ` ${sharedComboBoxStyles.flyinError}` : ` ${sharedComboBoxStyles.flyinSuccess}`}`}>
            {flyinMessage}
          </div>
        )}

        {imageDialogTarget && (
          <ImageDialog
            apiBase="/api/files"
            onSelect={this.handleImageDialogSelect}
            onCancel={() => this.setState({ imageDialogTarget: null })}
            folderSelectMode={imageDialogTarget === 'gallery'}
          />
        )}

        {showDeleteConfirm && (
          <div
            className={sharedComboBoxStyles.confirmOverlay}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                this.setState({ showDeleteConfirm: false });
              }
            }}
          >
            <div className={sharedComboBoxStyles.confirmDialog}>
              <h3>Seite wirklich löschen?</h3>
              <p>Diese Aktion kann nicht rückgängig gemacht werden.</p>
              <div className={sharedComboBoxStyles.confirmActions}>
                <button
                  type="button"
                  className="mw-button mw-button--ghost mw-button--red mw-button--small"
                  onClick={this.handleDeleteConfirm}
                >
                  <div className="mw-button-outer"><div className="mw-button-inner">Ja, löschen</div></div>
                </button>
                <button
                  type="button"
                  className="mw-button mw-button--ghost mw-button--small"
                  onClick={() => this.setState({ showDeleteConfirm: false })}
                >
                  <div className="mw-button-outer"><div className="mw-button-inner">Abbrechen</div></div>
                </button>
              </div>
            </div>
          </div>
        )}

        {isLoading && (
          <div className={sharedComboBoxStyles.inlineLoaderWrap}>
            <Spinner styles={{
              label: { color: UI_COLORS.text },
              circle: { 
                borderColor: `${UI_COLORS.accentBgHover} ${UI_COLORS.accent} ${UI_COLORS.text}`
              }}} size={SpinnerSize.large} label={'Wird geladen...'} />
          </div>
        )}

        {!isLoading && (
          <div className={sharedComboBoxStyles.inlineEditorContainer}>            
            {loadError && <div className={sharedComboBoxStyles.errorMsg}>{loadError}</div>}

            {!loadError && !isContentMod && <div className={sharedComboBoxStyles.errorMsg}>Keine Berechtigung zum Bearbeiten.</div>}

            {!loadError && isContentMod && this.renderForm()}
          </div>
        )}
      </div>
    );
  }
}
